export const CV = {
  handle: {
    resizeW: 8,
    resizeH: 8,
  },
  node: {
    // Mirrored by nodeBaseWidth / nodeHeight in internal/mcp/tools.go, which
    // sizes MCP-created nodes from their label. Change both or tool-made and
    // hand-made nodes end up different sizes on the same canvas.
    defaultW: 180,   // px — fits 11 characters without clipping
    defaultH: 53,    // px — node card height
    minW: 120,
    minH: 48,        // floor below the default so cards can still shrink on resize
    iconSize: 39,
    radius: 14,   // px — node card corner radius (per design mock)
    padX: 13,     // px — horizontal padding (design mock: 0 13px)
    gap: 11,      // px — flex gap between icon / text / badge (design mock)
    // Icon-tint wrap (per design mock): the node icon sits in a small rounded
    // box filled with its accent hue at low alpha. Tune the wrap + icon size here.
    iconWrapSize:   30,   // px — square icon-tint box
    iconWrapRadius: 9,    // px — corner radius of the box
    iconInWrapSize: 17,   // px — icon rendered inside the wrap
    // Info badge (file-text + page count) that sits inline at the node's right
    // edge (per design mock). Size the badge's file-text icon here.
    infoIconSize:   15,   // px — file-text glyph on the inline info badge
  },
  // Pages panel presentation (per design mock). Three "treatments" and the
  // camera framing used when a node's panel opens. Positions are relative to the
  // canvas area (the pane that already excludes the left rail).
  pagesPanel: {
    blurPx: 16,           // backdrop-blur strength on the panel card
    drawer: { bottom: 96, width: "min(680px, 90vw)", height: "46vh" }, // bottom sheet
    panel:  { top: 0, width: 430 },                                    // right side sheet
    glass:  { right: 40, width: 400, maxHeight: "78vh" },              // floating card
    // Camera framing when a panel opens: zoom in and place the node's centre at
    // (axf·vw, ayf·vh) so it isn't hidden behind the chosen treatment.
    camera: {
      zoom:   1.24,
      drawer: { axf: 0.5,  ayf: 0.16 },  // node lifted toward the top, sheet below
      panel:  { axf: 0.36, ayf: 0.46 },  // node shifted left, sheet on the right
      glass:  { axf: 0.42, ayf: 0.44 },  // node shifted left, card mid-right
      durationMs: 620,
    },
  },
  toolbar: {
    iconSize:        18,   // px — increase here to resize all toolbar icons
    iconStrokeWidth: 2.5,  // lucide strokeWidth — higher = bolder
    // Desktop floating bar: where it sits before it's ever dragged (top-center over
    // the canvas, mock parity), how close it may get to a screen edge once dragged,
    // and its stacking order (above the z-50 top bar so it can float over it).
    floatTop:        12,   // px — default distance from the top edge (mock `top:12px`)
    dragMargin:      8,    // px — min gap kept from every screen edge while dragging
    floatZ:          60,   // z-index of the floating bar (top bar is 50)
    // Mobile only: gap the bar keeps from the rail and screen edge (applied as a
    // max-width cap on the top-center Panel) so it never hides behind the nav
    // rail or touches the edge. Overflowing buttons scroll horizontally instead.
    mobileInset:     12,   // px — min side gap from the rail / screen edge (safety on narrow screens)
    mobileMaxWidth:  280,  // px — hard cap on bar width; the real "narrower" knob (lower = narrower, more scroll)
    mobileFadeWidth: 24,   // px — width of the left/right scroll-hint fade
  },
  controls: {
    // Extra px lifted above the mobile browser's bottom chrome / home indicator
    // so the zoom in/out/fit buttons aren't clipped off the bottom in landscape.
    // Added on top of env(safe-area-inset-bottom).
    mobileBottomLift: 112,
  },
  focusModal: {
    width:     "92vw",
    maxWidth:  "72rem",   // ~1152px — increase here to make wider
    height:    "88vh",    // fixed height — drives flex-1 editor fill
    maxHeight: "95vh",    // safety cap for very small screens
  },
  edge: {
    flowDotRadius: 3.4,  // px — radius of the animated dot that travels along edges (mock)
    endpointHandleRadius: 5,  // px — radius of the draggable endpoint dots shown on a selected edge
    // How close (px) the pointer must get to a connection handle for a new/dragged
    // edge to snap onto it. Generous so releasing anywhere over a node connects,
    // instead of the arrow "passing through" the shape without attaching.
    connectionRadius: 44,
  },
  shape: {
    minW: 60,
    minH: 60,
    defaults: {
      circle:        { width: 120, height: 120 },
      container_box: { width: 300, height: 200 },
      diamond:       { width: 140, height: 140 },
      hexagon:       { width: 140, height: 140 },
      cylinder:      { width: 120, height: 150 },
      // Flowchart / geometry
      pill:          { width: 160, height: 60 },
      ellipse:       { width: 180, height: 110 },
      pentagon:      { width: 130, height: 130 },
      octagon:       { width: 130, height: 130 },
      star:          { width: 130, height: 130 },
      offpage:       { width: 140, height: 120 },
      document:      { width: 180, height: 120 },
      // Annotation
      callout:       { width: 180, height: 120 },
      sticky_note:   { width: 150, height: 150 },
      brace:         { width: 40,  height: 160 },
      bracket:       { width: 40,  height: 160 },
      // Software architecture
      actor:         { width: 80,  height: 150 },
      cube:          { width: 160, height: 140 },
      swimlane:      { width: 360, height: 240 },
      datastore:     { width: 160, height: 110 },
      uml_component: { width: 180, height: 110 },
      uml_package:   { width: 180, height: 130 },
      fallback:      { width: 200, height: 100 },
    },
    // Default offset (endpoint B relative to endpoint A) for a freshly-placed
    // line/free_edge shape — not a width/height box, see LINE_SHAPE_KINDS.
    lineDefault: { dx: 120, dy: 0 },
    endpointHandleSize: 10,
  },
  // ReactFlow viewport zoom bounds — used by <Viewport> and by DrawingLayer's
  // two-finger pinch-zoom so both clamp identically.
  zoom: {
    min: 0.05,
    max: 4,
  },
  drawing: {
    minWidth: 1,              // px — thinnest pencil stroke
    maxWidth: 24,             // px — thickest pencil stroke
    highlighterOpacity: 0.35, // opacity for highlighter-mode strokes
    // Extra flow-units added to half the stroke width for eraser hit-testing —
    // makes thin strokes easier to erase.
    erasePadding: 6,
    // Ramer–Douglas–Peucker tolerance (flow units) applied to freehand strokes
    // on commit — drops redundant points without visibly changing the line.
    simplifyEpsilon: 1.5,
    // ReactFlow zIndex for highlighter strokes — lifts the translucent stroke
    // above node cards (default zIndex 0) so it reads as a marker overlay.
    highlighterZIndex: 1,
  },
} as const;
